"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = useIntersectionObserver;
require("intersection-observer");
var _react = require("react");
/**
 * Hook used to track if a target component is intersecting with its root.
 * @function useIntersectionObserver
 * @param {Object} [options] - Object of options.
 * @param {Element|Document} [options.root=null] - Ancestor of target whose bounding rectangle will be considered the viewport.
 * @param {String} [options.rootMargin='0px'] - Offsets to add to the root's bounding box when calculating intersections.
 * @param {Number|Array<Number>} [options.threshold=0] - Ratio of intersection area to total bounding box area of the target.
 * @param {Boolean} [options.disconnectWhenVisible=false] - Whether to disconnect the observer when the observed target is visible.
 * @param {Boolean} [options.enabled=true] - Whether to enable the IntersectionObserver.
 * @param {Function} [options.onChange] - A callback function to call when the intersection state changes. The intersection state will be passed as a parameter.
 * @example
 * const targetRef = useIntersectionObserver({
 *     onChange({isIntersecting}) {
 *         console.log('IntersectionObserver change:', isIntersecting);
 *     }
 * });
 *
 * <div ref={targetRef} />
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver|IntersectionObserver}
 * @returns {Function} targetRef - The function used to set the target ref node to observe.
 */
function useIntersectionObserver() {
  let {
    root = null,
    rootMargin = '0px',
    threshold = 0,
    disconnectWhenVisible = false,
    enabled = true,
    onChange
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const targetRef = (0, _react.useRef)(null);
  const observer = (0, _react.useRef)(enabled ? new IntersectionObserver(_ref => {
    let [newEntry] = _ref;
    if (onChange) {
      onChange(newEntry);
    }
    if (newEntry.isIntersecting && disconnectWhenVisible) {
      observer.current.disconnect();
    }
  }, {
    root,
    rootMargin,
    threshold
  }) : null);
  (0, _react.useEffect)(() => {
    const {
      current: obs
    } = observer;
    if (enabled && targetRef !== null && targetRef !== void 0 && targetRef.current) {
      obs.observe(targetRef.current);
    }
    return () => {
      if (enabled) {
        obs.disconnect();
      }
    };
  }, [enabled, targetRef]);
  return targetRef;
}